import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Link as LinkIcon,
  RefreshCw,
  Calendar,
  CheckCircle,
} from 'lucide-react';
import ConnectionHealthBadge from './ConnectionHealthBadge';
import SyncStatusBadge from './SyncStatusBadge';
import { usePlan } from '../context/PlanContext';
import styles from './CalendarConnectionsPanel.module.css';

const PROVIDER_LABELS = { google: 'Google Calendar', microsoft: 'Microsoft 365', apple: 'Apple Calendar' };
const providerLabel = (provider) => PROVIDER_LABELS[provider] || provider;

function formatRelativeTime(iso) {
  if (!iso) return 'never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function CalendarConnectionsPanel({
  calendarConnections,
  syncingProviders = {},
  syncing = false,
  onSyncProvider,
  onSyncAll,
  onConnectGoogle,
  onConnectMicrosoft,
  onConnectApple,
  onDisconnectProvider,
  onShowCalendarSelector,
  defaultExpanded = false,
}) {
  const { plan } = usePlan();
  const unhealthyConnections = (calendarConnections || []).filter(c => c.health && c.health !== 'ok');
  const hasUnhealthy = unhealthyConnections.length > 0;
  const [expanded, setExpanded] = useState(defaultExpanded);

  const FREE_MAX_CALENDARS = 1
  const maxCalendars = plan != null ? (plan.max_calendars ?? null) : FREE_MAX_CALENDARS
  const atCalendarLimit = maxCalendars !== null && calendarConnections.length >= maxCalendars;

  const showFull = hasUnhealthy || expanded || calendarConnections.length === 0;

  // Compact collapsed view
  if (calendarConnections.length > 0 && !showFull) {
    return (
      <div className={styles.collapsed}>
        {calendarConnections.map(conn => (
          <div key={conn.provider} className={styles.collapsedLine}>
            <CheckCircle size={16} className={styles.collapsedCheckIcon} />
            <span className={styles.collapsedProvider}>
              {providerLabel(conn.provider)}
            </span>
            <span className={styles.collapsedMeta}>
              Synced {formatRelativeTime(conn.last_synced_at || conn.synced_at)}
            </span>
          </div>
        ))}
        <button className={styles.manageLink} onClick={() => setExpanded(true)}>
          Manage
        </button>
      </div>
    );
  }

  // Full expanded view
  return (
    <div>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionHeaderLeft}>
          <LinkIcon className={styles.sectionIcon} size={24} />
          <h2 className={styles.sectionTitle}>Calendar Connections</h2>
        </div>
        {calendarConnections.length > 1 && (
          <button onClick={onSyncAll} disabled={syncing} className={styles.syncAllBtn}>
            <RefreshCw size={16} className={syncing ? styles.spinIcon : undefined} />
            {syncing ? 'Syncing All...' : 'Sync All'}
          </button>
        )}
      </div>

      {calendarConnections.length > 0 ? (
        <div className={styles.connectionList}>
          {calendarConnections.map((connection) => {
            const providerName = providerLabel(connection.provider);
            const isSyncingThis = syncingProviders[connection.provider];

            return (
              <div key={connection.provider} className={styles.providerCard}>
                <div className={styles.providerCardTop}>
                  <div className={styles.providerCardLeft}>
                    <div className={styles.providerIconWrap}>
                      <LinkIcon className={styles.providerIcon} size={20} />
                    </div>
                    <div>
                      <h3 className={styles.providerName}>{providerName}</h3>
                      <p className={styles.providerEmail}>{connection.email || 'Connected'}</p>
                    </div>
                  </div>
                  <ConnectionHealthBadge connection={connection} />
                </div>

                <SyncStatusBadge
                  status={isSyncingThis ? 'syncing' : (connection.sync_status || 'synced')}
                  lastSyncedAt={connection.last_synced_at || connection.synced_at}
                  error={connection.sync_error}
                />

                <div className={styles.providerActions}>
                  <button
                    onClick={() => onSyncProvider(connection.provider)}
                    disabled={isSyncingThis || syncing}
                    className={styles.syncBtn}
                  >
                    <RefreshCw size={16} className={isSyncingThis ? styles.spinIcon : undefined} />
                    {isSyncingThis ? 'Syncing...' : 'Sync Now'}
                  </button>
                  {onShowCalendarSelector && (
                    <button onClick={onShowCalendarSelector} className={styles.selectCalendarsBtn}>
                      <Calendar size={16} />
                      Select Calendars
                    </button>
                  )}
                  <button
                    onClick={() => onDisconnectProvider(connection.provider)}
                    disabled={syncing || isSyncingThis}
                    className={styles.disconnectBtn}
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            );
          })}

          {calendarConnections.length < 2 && (
            <div className={styles.connectAnotherCard}>
              <h3 className={styles.connectAnotherTitle}>Connect Another Calendar</h3>
              {atCalendarLimit ? (
                <p className={styles.connectAnotherDesc}>
                  You've reached your {plan?.name === 'free' ? 'Free' : plan?.name} plan limit of {maxCalendars} calendar{maxCalendars !== 1 ? 's' : ''}.{' '}
                  <Link to="/settings/billing" className={styles.upgradeLink}>Upgrade to Indie</Link> for up to 5.
                </p>
              ) : (
                <>
                  <p className={styles.connectAnotherDesc}>
                    Connect multiple calendars to see all your availability in one place.
                  </p>
                  <div className={styles.connectBtnsRow}>
                    {!calendarConnections.some(c => c.provider === 'google') && (
                      <button onClick={onConnectGoogle} className={styles.connectProviderBtn}>
                        <LinkIcon size={16} />
                        Connect Google Calendar
                      </button>
                    )}
                    {!calendarConnections.some(c => c.provider === 'microsoft') && (
                      <button onClick={onConnectMicrosoft} className={styles.connectProviderBtn}>
                        <LinkIcon size={16} />
                        Connect Microsoft 365
                      </button>
                    )}
                    {onConnectApple && !calendarConnections.some(c => c.provider === 'apple') && (
                      <button onClick={onConnectApple} className={styles.connectProviderBtn}>
                        <LinkIcon size={16} />
                        Connect Apple Calendar
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {expanded && !hasUnhealthy && (
            <button className={styles.manageLink} onClick={() => setExpanded(false)}>
              Collapse
            </button>
          )}
        </div>
      ) : (
        <div className={styles.noConnectionCard}>
          <p className={styles.noConnectionDesc}>
            Connect your calendar to sync your availability and find matching time slots.
          </p>
          <div className={styles.connectBtnsRow}>
            <button onClick={onConnectGoogle} className={styles.connectProviderBtn}>
              <LinkIcon size={16} />
              Connect Google Calendar
            </button>
            <button onClick={onConnectMicrosoft} className={styles.connectProviderBtn}>
              <LinkIcon size={16} />
              Connect Microsoft 365
            </button>
            {onConnectApple && (
              <button onClick={onConnectApple} className={styles.connectProviderBtn}>
                <LinkIcon size={16} />
                Connect Apple Calendar
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
